<template>

  <div>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div
        v-if="totalProducts==0"
        class="m-2"
      >
        <b-row>
          <b-col md="12">
            <div class="d-flex flex-column align-items-center">
              <feather-icon
                icon="BriefcaseIcon"
                size="70"
              />
              <h1 class="mt-2">
                Add products & services
              </h1>
              <span>Save your products and services to add them quickly on invoices and estimates.</span>
              <b-button
                class="mt-4"
                variant="primary"
                :to="{name:'products-add'}"
              >
                <span class="text-nowrap">{{ $t('Add Products') }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <div
        v-else
        class="m-2"
      >

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          />

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">

              <b-button

                variant="primary"
                :to="{name:'products-add'}"
              >
                <span class="text-nowrap">{{ $t('Add Product') }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

      </div>
      <div v-show="totalProducts>0">
        <b-table

          ref="refProductListTable"
          class="position-relative"
          :items="fetchTickets"
          responsive
          :fields="tableColumns"
          primary-key="id"
          show-empty
          striped
          hover
          selectable
          empty-text="No matching records found"
          @row-selected="onRowSelected"
        >
          <template #cell(name)="data">
            <span class="font-weight-bold d-block text-nowrap">{{ data.item.name }}</span>
            <small class="text-muted">{{ data.item.description }}</small>
          </template>

          <template #cell(price)="data">
            <span>{{ $user().currency.currency_code }} {{ $staticParams.formatCurrency(data.item.price) }}</span>

          </template>
          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >

              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>

              <b-dropdown-item
                variant="danger"
                @click="deleteData(data.item.id)"
              >
                <feather-icon icon="Trash2Icon" />
                <span class="align-middle ml-50">{{ $t('Delete') }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>

        </b-table>
        <div class="mx-2 mb-2">
          <b-row>

            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">{{ $t('Showing') }} {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} {{ $t('Entries') }}</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >

              <b-pagination
                v-model="currentPage"
                :total-rows="totalProducts"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BDropdown, BDropdownItem, BPagination, BButton,
} from 'bootstrap-vue'
import store from '@/store'
import Ripple from 'vue-ripple-directive'
import {
  ref, onUnmounted, watch, computed,
} from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import productStoreModule from './productStoreModule'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BButton,
  },
  directives: {
    Ripple,
  },
  data() {
    return {

    }
  },
  methods: {
    onRowSelected(items) {
      this.$router.push({ name: 'products-edit', params: { id: items[0].id } })
    },
    deleteData(id) {
      this.$swal({
        title: this.$t('Are you sure ?'),
        text: this.$t('You are about to delete a product. This action cannot be undone'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Yes, delete now'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          store.dispatch('app-product/deleteProduct', { id })
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Product is deleted',
                  icon: 'CheckIcon',
                  variant: 'success',
                },
              })
              this.$refs.refProductListTable.refresh()
            })
            .catch(() => {

            })
        }
      })
    },
  },
  setup() {
    const APP_STORE_MODULE_NAME = 'app-product'

    // Register module
    if (!store.hasModule(APP_STORE_MODULE_NAME)) store.registerModule(APP_STORE_MODULE_NAME, productStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(APP_STORE_MODULE_NAME)) store.unregisterModule(APP_STORE_MODULE_NAME)
    })

    const toast = useToast()

    const perPageOptions = [10, 25, 50, 100]
    const perPage = ref(10)
    const searchQuery = ref('')
    const totalProducts = ref(0)
    const refProductListTable = ref(null)
    const currentPage = ref(1)
    const tableColumns = [
      {
        key: 'name', label: 'PRODUCT NAME', sortable: true, thStyle: 'width:80%',
      },
      { key: 'price', label: 'PRICE', sortable: true },
      {
 key: 'actions', label: 'ACTIONS', thClass: 'text-right', tdClass: 'text-right',
},
    ]

    const refetchData = () => {
      refProductListTable.value.refresh()
    }

    watch([currentPage, perPage, searchQuery], () => {
      refetchData()
    })
    const dataMeta = computed(() => {
      const localItemsCount = refProductListTable.value ? refProductListTable.value.localItems.length : 0
      return {
        from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
        to: perPage.value * (currentPage.value - 1) + localItemsCount,
        of: totalProducts.value,
      }
    })
    const fetchTickets = (ctx, callback) => {
      store
        .dispatch('app-product/fetchProducts', {
          q: searchQuery.value,
          perPage: perPage.value,
          page: currentPage.value,
        })
        .then(response => {
          const { data } = response.data.data
          const { total } = response.data.meta
          callback(data)
          totalProducts.value = total
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error fetching tickets list',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }
    return {

      tableColumns,
      // Filter
      perPageOptions,
      perPage,
      dataMeta,
      fetchTickets,
      searchQuery,
      totalProducts,
      currentPage,
      refetchData,
      // UI
      refProductListTable,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
